import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Plus, PackageMinus, PackagePlus, Trash2, History, ImagePlus, AlertTriangle, Download, FileText } from "lucide-react";
import { useSearchParams } from "react-router-dom";
import api, { formatApiErrorDetail } from "../lib/api";
import { useLang } from "../context/LangContext";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../components/ui/sheet";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import SignaturePad from "../components/SignaturePad";
import ReservationPanel from "../components/ReservationPanel";
import { OverdueBanner, EquipmentGrid, MovementHistorySheet } from "../components/EquipmentParts";
import { enqueueMovement, isNetworkError } from "../lib/offlineQueue";

function PhotoUpload({ value, onChange, testid }) {
  const ref = useRef();
  const [busy, setBusy] = useState(false);
  const url = value ? `${api.defaults.baseURL}/files/${value.storage_path}?auth=${localStorage.getItem("access_token") || ""}` : null;
  const handle = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };
  return (
    <>
      <input ref={ref} type="file" accept="image/*" onChange={handle} hidden />
      <button
        type="button"
        data-testid={testid}
        onClick={() => ref.current.click()}
        className="w-full aspect-video rounded-xl overflow-hidden bg-white/5 border border-dashed border-white/10 hover:bg-white/10 flex items-center justify-center relative"
      >
        {url ? (
          <img src={url} alt="" className="w-full h-full object-cover" />
        ) : (
          <div className="text-slate-400 text-sm flex items-center gap-2">
            <ImagePlus className="w-4 h-4" /> {busy ? "…" : "Photo"}
          </div>
        )}
      </button>
    </>
  );
}

export default function Equipment() {
  const { t } = useLang();
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [openCreate, setOpenCreate] = useState(false);
  const [selected, setSelected] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState([]);
  const [overdue, setOverdue] = useState([]);
  const [params, setParams] = useSearchParams();

  const load = useCallback(async () => {
    const [eq, od] = await Promise.all([api.get("/equipment"), api.get("/movements/overdue")]);
    setItems(eq.data);
    setOverdue(od.data);
  }, []);
  useEffect(() => {
    load();
    window.addEventListener("movements-synced", load);
    return () => window.removeEventListener("movements-synced", load);
  }, [load]);

  useEffect(() => {
    const id = params.get("open");
    if (!id || !items.length) return;
    const e = items.find((x) => x.id === id);
    if (e) { setSelected(e); setParams({}, { replace: true }); }
  }, [items, params, setParams]);

  const overdueByEq = useMemo(() => {
    const m = {};
    for (const o of overdue) m[o.equipment_id] = (m[o.equipment_id] || 0) + o.quantity;
    return m;
  }, [overdue]);

  const exportCsv = async () => {
    try {
      const { data } = await api.get("/movements/export", { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url; a.download = `mouvements_${new Date().toISOString().slice(0, 10)}.csv`; a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const openHistory = async () => {
    const { data } = await api.get("/movements");
    setHistory(data);
    setShowHistory(true);
  };

  const isAdmin = user?.role === "admin";
  const canManage = isAdmin || user?.role === "agent";

  return (
    <div className="space-y-6" data-testid="equipment-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight" style={{ fontFamily: "Outfit" }}>{t("equipment")}</h1>
          <p className="text-slate-400 mt-1 text-sm">{items.length} · {t("kpi_types")}</p>
        </div>
        <div className="flex gap-2">
          {isAdmin && (
            <Button variant="outline" onClick={exportCsv} data-testid="equipment-export-csv-btn" className="border-white/10 bg-white/5">
              <Download className="w-4 h-4 mr-1.5" />
              {t("export_csv")}
            </Button>
          )}
          <Button variant="outline" onClick={openHistory} data-testid="equipment-history-btn" className="border-white/10 bg-white/5">
            <History className="w-4 h-4 mr-1.5" />
            {t("movement_history")}
          </Button>
          {canManage && <CreateEquipmentDialog open={openCreate} setOpen={setOpenCreate} onCreated={load} />}
        </div>
      </div>

      <OverdueBanner overdue={overdue} />
      <EquipmentGrid items={items} overdueByEq={overdueByEq} onSelect={setSelected} />

      <EquipmentSheet eq={selected} onClose={() => setSelected(null)} onChanged={load} canManage={canManage} userName={user?.name} isAdmin={isAdmin} currentUser={user} />
      <MovementHistorySheet open={showHistory} onOpenChange={setShowHistory} history={history} canDelete={isAdmin} onChanged={() => { openHistory(); load(); }} />
    </div>
  );
}

function CreateEquipmentDialog({ open, setOpen, onCreated }) {
  const { t } = useLang();
  const [form, setForm] = useState({ name: "", category: "", quantity: 1, description: "", photo: null });
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/equipment", form);
      toast.success("OK");
      setOpen(false);
      setForm({ name: "", category: "", quantity: 1, description: "", photo: null });
      onCreated();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="equipment-add-button" className="btn-glow bg-indigo-500 hover:bg-indigo-600">
          <Plus className="w-4 h-4 mr-1.5" /> {t("add_equipment")}
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-slate-900 border-white/10 max-w-md">
        <DialogHeader><DialogTitle>{t("add_equipment")}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <PhotoUpload testid="equipment-photo-upload" value={form.photo} onChange={(f) => setForm({ ...form, photo: f })} />
          <div>
            <Label>{t("name")}</Label>
            <Input data-testid="equipment-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 bg-white/5 border-white/10" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>{t("category")}</Label>
              <Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="mt-1 bg-white/5 border-white/10" />
            </div>
            <div>
              <Label>{t("quantity")}</Label>
              <Input type="number" min={1} value={form.quantity} onChange={(e) => setForm({ ...form, quantity: parseInt(e.target.value || "1", 10) })} className="mt-1 bg-white/5 border-white/10" />
            </div>
          </div>
          <div>
            <Label>{t("description")}</Label>
            <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1 bg-white/5 border-white/10" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)}>{t("cancel")}</Button>
          <Button data-testid="equipment-add-submit" onClick={submit} disabled={busy || !form.name} className="bg-indigo-500 hover:bg-indigo-600">{busy ? "…" : t("save")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EquipmentSheet({ eq, onClose, onChanged, canManage, userName, isAdmin, currentUser }) {
  const { t } = useLang();
  const [action, setAction] = useState("out");
  const [form, setForm] = useState({ quantity: 1, borrower_name: "", purpose: "", expected_return: "", notes: "", condition_photo: null });
  const [busy, setBusy] = useState(false);
  const [signature, setSignature] = useState(null);

  useEffect(() => {
    if (eq) { setForm({ quantity: 1, borrower_name: userName || "", purpose: "", expected_return: "", notes: "", condition_photo: null }); setSignature(null); }
  }, [eq, userName]);

  if (!eq) return null;

  const submit = async () => {
    if (action === "out" && !signature) { toast.error(t("signature_required")); return; }
    setBusy(true);
    const payload = { equipment_id: eq.id, action, ...form };
    const queueIt = () => {
      enqueueMovement(payload, action === "out" ? signature : null);
      window.dispatchEvent(new Event("offline-queue-changed"));
      toast.warning(t("offline_saved"));
      onClose();
    };
    if (!navigator.onLine) { queueIt(); setBusy(false); return; }
    try {
      let sig = null;
      if (action === "out" && signature) {
        const blob = await (await fetch(signature)).blob();
        const fd = new FormData();
        fd.append("file", blob, `signature-${Date.now()}.png`);
        const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
        sig = data;
      }
      const { data: mvDoc } = await api.post("/movements", { equipment_id: eq.id, action, ...form, signature: sig });
      if (action === "out") {
        window.open(`${api.defaults.baseURL}/movements/${mvDoc.id}/receipt?auth=${localStorage.getItem("access_token") || ""}`, "_blank");
      }
      toast.success("OK");
      onChanged();
      onClose();
    } catch (err) {
      if (isNetworkError(err)) queueIt();
      else toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const remove = async () => {
    await api.delete(`/equipment/${eq.id}`);
    onChanged(); onClose();
  };

  const photoUrl = eq.photo ? `${api.defaults.baseURL}/files/${eq.photo.storage_path}?auth=${localStorage.getItem("access_token") || ""}` : null;

  return (
    <Sheet open={!!eq} onOpenChange={(v) => !v && onClose()}>
      <SheetContent className="bg-slate-900 border-white/10 w-full sm:max-w-lg overflow-y-auto">
        <SheetHeader><SheetTitle>{eq.name}</SheetTitle></SheetHeader>
        <div className="mt-4 space-y-4">
          {photoUrl && <img src={photoUrl} alt="" className="w-full aspect-video object-cover rounded-xl" />}
          <div className="flex gap-2 flex-wrap">
            <Badge variant="outline" className="border-white/10 bg-white/5">{eq.category}</Badge>
            <Badge variant="outline" className="border-emerald-500/30 bg-emerald-500/10 text-emerald-300">
              {eq.available}/{eq.quantity} {t("available").toLowerCase()}
            </Badge>
          </div>
          {eq.description && <p className="text-sm text-slate-300">{eq.description}</p>}

          <div className="glass rounded-xl p-4 space-y-3">
            <div className="flex gap-2">
              <Button
                size="sm"
                data-testid="equipment-checkout-button"
                onClick={() => setAction("out")}
                className={action === "out" ? "bg-rose-500 hover:bg-rose-600" : "bg-white/5 hover:bg-white/10 text-slate-300"}
              >
                <PackageMinus className="w-4 h-4 mr-1.5" /> {t("check_out")}
              </Button>
              <Button
                size="sm"
                data-testid="equipment-checkin-button"
                onClick={() => setAction("in")}
                className={action === "in" ? "bg-emerald-500 hover:bg-emerald-600" : "bg-white/5 hover:bg-white/10 text-slate-300"}
              >
                <PackagePlus className="w-4 h-4 mr-1.5" /> {t("check_in")}
              </Button>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs text-slate-400">{t("quantity")}</Label>
                <Input type="number" min={1} value={form.quantity} onChange={(e) => setForm({ ...form, quantity: parseInt(e.target.value || "1", 10) })} className="bg-white/5 border-white/10 mt-1" />
              </div>
              <div>
                <Label className="text-xs text-slate-400">{t("expected_return")}</Label>
                <Input type="date" value={form.expected_return} onChange={(e) => setForm({ ...form, expected_return: e.target.value })} className="bg-white/5 border-white/10 mt-1" />
              </div>
            </div>
            <div>
              <Label className="text-xs text-slate-400">{t("borrower")}</Label>
              <Input value={form.borrower_name} onChange={(e) => setForm({ ...form, borrower_name: e.target.value })} className="bg-white/5 border-white/10 mt-1" />
            </div>
            <div>
              <Label className="text-xs text-slate-400">{t("purpose")}</Label>
              <Input value={form.purpose} onChange={(e) => setForm({ ...form, purpose: e.target.value })} className="bg-white/5 border-white/10 mt-1" />
            </div>
            <div>
              <Label className="text-xs text-slate-400">Notes</Label>
              <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="bg-white/5 border-white/10 mt-1" />
            </div>
            <PhotoUpload testid="movement-photo-upload" value={form.condition_photo} onChange={(f) => setForm({ ...form, condition_photo: f })} />
            {action === "out" && <SignaturePad key={eq.id} onChange={setSignature} />}
            <Button data-testid="movement-submit-btn" onClick={submit} disabled={busy} className="w-full bg-indigo-500 hover:bg-indigo-600">
              {busy ? "…" : t("save")}
            </Button>
          </div>

          <ReservationPanel equipmentId={eq.id} currentUser={currentUser} />

          {isAdmin && (
            <Button variant="destructive" size="sm" onClick={remove} data-testid="equipment-delete-btn">
              <Trash2 className="w-3.5 h-3.5 mr-1.5" /> {t("delete")}
            </Button>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
